import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { crossBorderAPI } from '../../api';
import { toastApiError } from '../../lib/utils';

export const EVENT_OPTIONS = [
  { id: 'origin_arrival', label: 'Record Border Arrival (origin side)' },
  { id: 'origin_departure', label: 'Record Border Departure (origin side)' },
  { id: 'crossing_confirmed', label: 'Confirm Crossing' },
  { id: 'destination_arrival', label: 'Record Border Arrival (destination side)' },
  { id: 'destination_departure', label: 'Record Border Departure (destination side)' },
  { id: 'expected_crossing', label: 'Set Crossing ETA (expected — does not change stage)' },
];
const DOC_STATE_LABEL = { none: 'Not received', uploaded: 'Uploaded', reviewed: 'Reviewed', valid: 'Valid', expired: 'Expired / replaced' };

const nowLocal = () => {
  const d = new Date();
  d.setSeconds(0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-xs font-semibold text-[var(--tms-text-secondary)]">{label}{children}</label>
);
const inputCls = 'h-9 px-2 rounded-md border border-[var(--tms-border)] bg-[var(--tms-card)] text-sm font-normal text-[var(--tms-text)]';

// Record a timestamp-backed milestone (event time + location + time zone)
export const RecordMilestoneDialog = ({ open, onOpenChange, load, meta, initialType, onSaved }) => {
  const [type, setType] = useState(initialType || 'origin_arrival');
  const [at, setAt] = useState(nowLocal());
  const [locationId, setLocationId] = useState('');
  const [tz, setTz] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const locations = meta?.locations || [];
  const openCrossing = (load.cb?.crossings || []).find(c => !c.complete) || null;

  useEffect(() => {
    if (!open) return;
    setType(initialType || 'origin_arrival');
    setAt(nowLocal());
    setLocationId(openCrossing?.location_id || '');
    setTz('');
    setNote('');
  }, [open, initialType, openCrossing?.location_id]);

  const loc = locations.find(l => l.id === locationId);
  const side = type.startsWith('destination') ? (openCrossing?.destination_country || load.cb?.direction?.split('→')[1]) : (openCrossing?.origin_country || load.cb?.direction?.split('→')[0]);
  const defaultTz = loc?.tz?.[side] || meta?.operational_tz || 'America/Chicago';

  const submit = async () => {
    if (!at) return toast.error('Event time is required');
    if (!locationId && !openCrossing?.location_id) return toast.error('Select the crossing location');
    setBusy(true);
    try {
      const res = await crossBorderAPI.recordEvent(load.id, { type, at_local: at, timezone: tz || defaultTz, location_id: locationId || undefined, location_name: loc?.name, crossing_id: openCrossing?.id, note: note || undefined });
      toast.success('Milestone recorded');
      onSaved(res.data);
      onOpenChange(false);
    } catch (e) { toastApiError(toast, e, 'Milestone could not be recorded'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cb-milestone-dialog">
        <DialogHeader><DialogTitle>Record border milestone</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Field label="Milestone">
            <select className={inputCls} value={type} onChange={(e) => setType(e.target.value)} data-testid="cb-milestone-type">
              {EVENT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Crossing location">
            <select className={inputCls} value={locationId} onChange={(e) => setLocationId(e.target.value)} data-testid="cb-milestone-location">
              <option value="">Select…</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Event time (local)">
              <input type="datetime-local" className={inputCls} value={at} onChange={(e) => setAt(e.target.value)} data-testid="cb-milestone-time" />
            </Field>
            <Field label="Time zone">
              <input className={inputCls} value={tz} placeholder={defaultTz} onChange={(e) => setTz(e.target.value)} data-testid="cb-milestone-tz" />
            </Field>
          </div>
          <Field label="Note (optional)">
            <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} data-testid="cb-milestone-note" />
          </Field>
          <p className="text-[11px] text-[var(--tms-text-muted)]">Stored in UTC · shown in Central and location time. Actual milestones cannot be in the future.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="cb-milestone-submit">{busy ? 'Saving…' : 'Record'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Correct (or void) a recorded milestone — requires reason, elevated roles only
export const CorrectMilestoneDialog = ({ open, onOpenChange, load, onSaved }) => {
  const events = (load.cb?.crossings || []).flatMap(c => (c.events || []).filter(e => !e.voided).map(e => ({ ...e, crossing: `${c.origin_country}→${c.destination_country}` })));
  const [eventId, setEventId] = useState('');
  const [at, setAt] = useState('');
  const [reason, setReason] = useState('');
  const [voidIt, setVoidIt] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setEventId(events[0]?.id || ''); setAt(''); setReason(''); setVoidIt(false); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const ev = events.find(e => e.id === eventId);

  const submit = async () => {
    if (!reason.trim()) return toast.error('A reason is required');
    if (!voidIt && !at) return toast.error('Enter the corrected time or choose Remove');
    setBusy(true);
    try {
      const res = await crossBorderAPI.correctEvent(load.id, eventId, { at_local: voidIt ? undefined : at, timezone: ev?.timezone, reason });
      toast.success(voidIt ? 'Milestone removed and stage recalculated' : 'Milestone corrected and stage recalculated');
      onSaved(res.data);
      onOpenChange(false);
    } catch (e) { toastApiError(toast, e, 'Correction failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cb-correct-dialog">
        <DialogHeader><DialogTitle>Correct milestone</DialogTitle></DialogHeader>
        {events.length === 0 ? <p className="text-sm text-[var(--tms-text-muted)]">No recorded milestones to correct.</p> : (
          <div className="grid gap-3">
            <Field label="Milestone">
              <select className={inputCls} value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="cb-correct-event">
                {events.map(e => <option key={e.id} value={e.id}>{e.crossing} · {e.type.replace(/_/g, ' ')} · {new Date(e.at).toLocaleString('en-US', { timeZone: e.timezone || 'America/Chicago' })}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={voidIt} onChange={(e) => setVoidIt(e.target.checked)} data-testid="cb-correct-void" /> Remove this milestone (no replacement time)</label>
            {!voidIt && (
              <Field label={`Corrected time (${ev?.timezone || 'local'})`}>
                <input type="datetime-local" className={inputCls} value={at} onChange={(e) => setAt(e.target.value)} data-testid="cb-correct-time" />
              </Field>
            )}
            <Field label="Reason (required, audited)">
              <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cb-correct-reason" />
            </Field>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {events.length > 0 && <Button onClick={submit} disabled={busy} data-testid="cb-correct-submit">Apply correction</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Review crossing documents: uploaded / reviewed / valid / expired per document
export const ReviewDocumentsDialog = ({ open, onOpenChange, load, onSaved }) => {
  const details = load.cb?.docs?.details || {};
  const [busyKey, setBusyKey] = useState('');
  const setState = async (key, status) => {
    setBusyKey(key);
    try {
      const res = await crossBorderAPI.setDocState(load.id, key, { status });
      onSaved(res.data);
    } catch (e) { toastApiError(toast, e, 'Document state could not be saved'); } finally { setBusyKey(''); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cb-docs-dialog">
        <DialogHeader><DialogTitle>Review crossing documents · {load.cb?.direction_label || 'direction unknown'}</DialogTitle></DialogHeader>
        <p className="text-xs text-[var(--tms-text-muted)]">Readiness: <strong>{load.cb?.docs?.summary}</strong>. Document readiness never changes the border stage.</p>
        <div className="divide-y divide-[var(--tms-border)]">
          {Object.entries(details).map(([key, d]) => (
            <div key={key} className="flex items-center justify-between gap-3 py-2" data-testid={`cb-doc-row-${key}`}>
              <div>
                <div className="text-sm font-semibold">{d.label}{d.required && <span className="ml-1 text-[10px] uppercase text-[var(--tms-primary)]">required</span>}</div>
                <div className="text-[11px] text-[var(--tms-text-muted)]">{DOC_STATE_LABEL[d.status || 'none']}{d.by_name ? ` · ${d.by_name}` : ''}{d.at ? ` · ${new Date(d.at).toLocaleString('en-US', { timeZone: 'America/Chicago' })} CT` : ''}</div>
              </div>
              <select className={inputCls} value={d.status || 'none'} disabled={busyKey === key} onChange={(e) => setState(key, e.target.value)} data-testid={`cb-doc-state-${key}`}>
                {Object.entries(DOC_STATE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={() => onOpenChange(false)}>Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Admin override of the stage (with reason) or return to automatic
export const OverrideStageDialog = ({ open, onOpenChange, load, meta, onSaved }) => {
  const [stage, setStage] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setStage(load.cb?.stage || ''); setReason(''); } }, [open, load.cb?.stage]);
  const active = !!load.cb?.override;

  const run = async (fn, msg) => {
    if (!reason.trim()) return toast.error('A reason is required');
    setBusy(true);
    try { const res = await fn(); toast.success(msg); onSaved(res.data); onOpenChange(false); }
    catch (e) { toastApiError(toast, e, 'Override failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cb-override-dialog">
        <DialogHeader><DialogTitle>{active ? 'Stage override active' : 'Override Cross-Border Stage'}</DialogTitle></DialogHeader>
        {active && (
          <div className="text-xs rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-900" data-testid="cb-override-info">
            {load.cb.override.previous || '—'} → <strong>{load.cb.override.new}</strong> by {load.cb.override.user_name} · {new Date(load.cb.override.at).toLocaleString('en-US', { timeZone: 'America/Chicago' })} CT<br />Reason: {load.cb.override.reason}
          </div>
        )}
        <div className="grid gap-3">
          {!active && (
            <Field label="Stage">
              <select className={inputCls} value={stage} onChange={(e) => setStage(e.target.value)} data-testid="cb-override-stage">
                <option value="">Select…</option>
                {(meta?.stages || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          )}
          <Field label="Reason (required, audited)">
            <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cb-override-reason" />
          </Field>
          <p className="text-[11px] text-[var(--tms-text-muted)]">While overridden, new events are recorded but do not change the stage until you return to automatic.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {active
            ? <Button onClick={() => run(() => crossBorderAPI.clearOverride(load.id, reason), 'Returned to automatic stage')} disabled={busy} data-testid="cb-override-clear">Return to Automatic</Button>
            : <Button onClick={() => run(() => crossBorderAPI.setOverride(load.id, { stage, reason }), 'Stage overridden')} disabled={busy || !stage} data-testid="cb-override-submit">Apply override</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
